import { Alert, buttonClassName } from "@opowiedzo/ui";
import { useCustomMutation, useGetIdentity, useInvalidate, useTable } from "@refinedev/core";
import { Link, useSearchParams } from "react-router";

import { Badge } from "@/components/Badge";
import { LinkButton, SmallButton } from "@/components/Form";
import { RetryIcon, SendIcon, SkipIcon } from "@/components/Icon";
import {
  ErrorBox,
  FilterBar,
  Loading,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
} from "@/components/Page";
import { EmptyRow, Table, Td, Th, Tr } from "@/components/Table";
import {
  CHANNEL_OPTIONS,
  DELIVERY_STATUS_OPTIONS,
  channelLabel,
  deliveryStatus,
  formatDateTime,
} from "@/lib/format";
import { filterValue } from "@/lib/query";
import type { AdminIdentity } from "@/providers/authProvider";

export interface DeliveryRow extends Record<string, unknown> {
  id: string;
  memoir_title: string | null;
  question: string | null;
  channel: string;
  status: string;
  scheduled_at: string;
  sent_at: string | null;
  reminder_count: number;
  failed_reason: string | null;
  story_id: string | null;
}

export function PromptDeliveriesPage() {
  // Przegląd i detal Opowieści linkują tu z gotowymi filtrami, więc adres
  // jest stanem początkowym tabeli.
  const [searchParams] = useSearchParams();
  const zAdresu = [
    ...(searchParams.get("failures_only") === "true"
      ? [{ field: "failures_only", operator: "eq" as const, value: "true" }]
      : []),
    ...(searchParams.get("memoir_id")
      ? [{ field: "memoir_id", operator: "eq" as const, value: searchParams.get("memoir_id")! }]
      : []),
  ];

  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<DeliveryRow>({
      resource: "prompt-deliveries",
      sorters: { initial: [{ field: "scheduled_at", order: "desc" }] },
      filters: { initial: zAdresu.length > 0 ? zAdresu : undefined },
    });

  const invalidate = useInvalidate();
  const { data: identity } = useGetIdentity<AdminIdentity | null>();
  const { mutate, mutation } = useCustomMutation();
  const mozeDzialac = (identity?.permissions ?? []).includes("deliveries.act");

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  // Ta sama akcja co na detalu wysyłki. Wiersz z błędem jest miejscem, w
  // którym człowiek ją podejmuje, więc nie każemy mu wchodzić głębiej.
  const akcja = (deliveryId: string, sciezka: "resend" | "skip") =>
    mutate(
      {
        url: `/prompt-deliveries/${deliveryId}/${sciezka}`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => invalidate({ resource: "prompt-deliveries", invalidates: ["list"] }) },
    );

  return (
    <>
      <PageHeader
        title="Wysyłki pytań"
        description="Każde pytanie, które wyszło albo miało wyjść. Cisza po stronie narratora może znaczyć i brak czasu, i mail w spamie - powód błędu jest w ostatniej kolumnie."
        actions={
          mozeDzialac ? (
            <Link
              to="/wyslij-pytanie"
              className={buttonClassName({ variant: "primary", size: "sm" })}
            >
              <SendIcon />
              Wyślij pytanie teraz
            </Link>
          ) : undefined
        }
      />

      <FilterBar>
        <Select
          label="Status"
          value={filterValue(filters, "status")}
          options={DELIVERY_STATUS_OPTIONS}
          onChange={(value) => ustawFiltr("status", value)}
        />
        <Select
          label="Kanał"
          value={filterValue(filters, "channel")}
          options={CHANNEL_OPTIONS}
          onChange={(value) => ustawFiltr("channel", value)}
        />
        <SearchInput
          label="Szukaj"
          placeholder="treść pytania, tytuł Opowieści"
          value={filterValue(filters, "query")}
          onChange={(value) => ustawFiltr("query", value)}
        />
      </FilterBar>

      <ErrorBox error={tableQuery.error} />
      {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Zaplanowane</Th>
                <Th>Opowieść</Th>
                <Th>Pytanie</Th>
                <Th>Kanał</Th>
                <Th>Status</Th>
                <Th>Powód błędu</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={6}>Żadna wysyłka nie pasuje do tych filtrów.</EmptyRow>
              )}
              {result.data.map((delivery) => {
                const status = deliveryStatus(delivery.status);
                const nieudana = delivery.status === "failed";
                return (
                  <Tr key={delivery.id}>
                    <Td nowrap>
                      <Link
                        to={`/wysylki-pytan/${delivery.id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {formatDateTime(delivery.scheduled_at)}
                      </Link>
                    </Td>
                    <Td nowrap>{delivery.memoir_title ?? "-"}</Td>
                    <Td>{delivery.question ?? "-"}</Td>
                    <Td nowrap>{channelLabel(delivery.channel)}</Td>
                    <Td>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </Td>
                    <Td>
                      <span className="text-ink-soft">{delivery.failed_reason ?? "-"}</span>
                      {nieudana && mozeDzialac && (
                        <span className="mt-1.75 flex items-center gap-1.5">
                          <SmallButton
                            disabled={mutation.isPending}
                            onClick={() => akcja(delivery.id, "resend")}
                          >
                            <RetryIcon className="size-3.5 shrink-0" />
                            Wyślij ponownie
                          </SmallButton>
                          <LinkButton
                            disabled={mutation.isPending}
                            onClick={() => akcja(delivery.id, "skip")}
                          >
                            <SkipIcon className="size-3.5 shrink-0" />
                            Pomiń
                          </LinkButton>
                        </span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
